import { createSelector } from 'reselect';

import {
  StateDictionary,
  StringStateDictionary,
  RequestState,
} from '../reducers/utils/state-manager';

type UserActivitySubscriptionDictionary = {
  [accountId: number]: AccountUserActivitySubscription[];
};
type AccountActivityNotificationDictionary = { [accountId: number]: AccountActivityNotification[] };

type AccountDictionary = { [accountId: number]: TeamEngineAccount };
type BillingInfoDictionary = { [accountId: number]: StripeBillingInfo };
type MessageServiceStatusDictionary = { [accountId: number]: MessageServiceStatus[] };
type CancellationDetailsDictionary = { [accountId: number]: AccountCancellationDetails };
type MessageCountsDictionary = { [accountId: number]: AccountMessageCounts };

export type AccountState = {
  accountsById: AccountDictionary;
  accountsStateById: StateDictionary;
  accountsStateByUser: StringStateDictionary;
  userActivitySubscriptionsByAccountId: UserActivitySubscriptionDictionary;
  userActivitySubscriptionsByAccountIdStatus: StateDictionary;
  billingInfoById: BillingInfoDictionary;
  billingInfoStateByAccountId: StateDictionary;
  stripeBasePlans: StripePlan[] | null;
  stripeBasePlansStatus: RequestState;
  stripeBasePricesV3: StripePriceV3[] | null;
  stripeBasePricesV3Status: RequestState;
  accountActivityNotificationsByAccountId: AccountActivityNotificationDictionary;
  accountActivityNotificationsByAccountIdStatus: StateDictionary;
  messageServiceStatusById: MessageServiceStatusDictionary;
  messageServiceStatusStateByAccountId: StateDictionary;
  cancellationDetailsByAccountId: CancellationDetailsDictionary;
  cancellationDetailsStateByAccountId: StateDictionary;
  messageCountsByAccountId: MessageCountsDictionary;
  messageCountsStateByAccountId: StateDictionary;
};

type State = { account: AccountState };

const accountsByIdSelector = (state: State) => state.account.accountsById;
export const accountsSelector = createSelector([accountsByIdSelector], (accountsById) =>
  Object.values(accountsById),
);

export const accountSelector = createSelector(
  [accountsByIdSelector, (_state: State, accountId: number) => accountId],
  (accountsById, accountId) => accountsById[accountId],
);

export const stripeBasePlansSelector = (state: State) => state.account.stripeBasePlans;
export const stripeBasePlansStatusSelector = (state: State) => state.account.stripeBasePlansStatus;
export const stripeBasePlanSelector = createSelector(
  [stripeBasePlansSelector, (_state: State, basePlanId: string) => basePlanId],
  (stripeBasePlans, basePlanId) =>
    stripeBasePlans ? stripeBasePlans.filter((bp) => bp.id === basePlanId)[0] : null,
);

export const stripeBasePricesSelectorV3 = (state: State) => state.account.stripeBasePricesV3;
export const stripeBasePricesStatusSelectorV3 = (state: State) =>
  state.account.stripeBasePricesV3Status;
export const stripeBasePriceSelectorV3 = createSelector(
  [stripeBasePricesSelectorV3, (_state: State, basePriceId: string) => basePriceId],
  (stripeBasePrices, basePlanId) =>
    stripeBasePrices ? stripeBasePrices.filter((bp) => bp.id === basePlanId)[0] : null,
);

// A little weird here, but we want to load all accounts for a user. Doesn't really matter what the key
// is, and it's a little annoying to get the username, so instead just use a static 'user' key to indicate
// the state of the accounts loaded by user
export const accountsByIdStatusSelector = (state: State) => state.account.accountsStateByUser.user;
export const accountsStateByIdSelector = (state: State) => state.account.accountsStateById;
export const accountsStatusSelector = createSelector(
  [accountsStateByIdSelector, (_state: State, accountId: number) => accountId],
  (accountsStateById: StateDictionary, accountId: number): RequestState =>
    accountsStateById[accountId],
);

export const userActivitySubscriptionsSelector = (state: State, accountId: number) =>
  state.account.userActivitySubscriptionsByAccountId[accountId];
export const userActivitySubscriptionsByAccountIdStatusSelector = (state: State) =>
  state.account.userActivitySubscriptionsByAccountIdStatus;
export const userActivitySubscriptionsForAccountIdStatusSelector = createSelector(
  [
    userActivitySubscriptionsByAccountIdStatusSelector,
    (_state: State, accountId: number) => accountId,
  ],
  (userActivitySubscriptionsByAccountIdStatus, accountId) =>
    userActivitySubscriptionsByAccountIdStatus[accountId],
);

export const billingInfoForAccountIdSelector = createSelector(
  [
    (state: State) => state.account.billingInfoById,
    (_state: State, accountId: number) => accountId,
  ],
  (billingInfoById, accountId) => billingInfoById[accountId],
);
export const billingInfoStateForAccountIdSelector = createSelector(
  [
    (state: State) => state.account.billingInfoStateByAccountId,
    (_state: State, accountId: number) => accountId,
  ],
  (billingInfoStateByAccountId, accountId) => billingInfoStateByAccountId[accountId],
);

// until we implement other types of account activity notifications, a user will only have 1 or 0 notification
export const accountActivityNotificationsForUser = (
  state: State,
  accountId: number,
  username: string,
) => {
  const accountActivityNotificationsForAccountId =
    state.account.accountActivityNotificationsByAccountId[accountId];
  if (!accountActivityNotificationsForAccountId) {
    return undefined;
  }
  return accountActivityNotificationsForAccountId.find((n) => n.username === username) || null;
};
export const accountActivityNotificationsByAccountIdStatusSelector = (
  state: State,
  accountId: number,
) => state.account.accountActivityNotificationsByAccountIdStatus[accountId];

export const messageServiceStatusForAccountIdSelector = createSelector(
  [
    (state: State) => state.account.messageServiceStatusById,
    (_state: State, accountId: number) => accountId,
  ],
  (messageServiceStatusById, accountId) => messageServiceStatusById[accountId],
);
export const messageServiceStatusStateForAccountIdSelector = createSelector(
  [
    (state: State) => state.account.messageServiceStatusStateByAccountId,
    (_state: State, accountId: number) => accountId,
  ],
  (messageServiceStatusStateByAccountId, accountId) =>
    messageServiceStatusStateByAccountId[accountId],
);

const cancellationDetailsStateByAccountIdSelector = (state: State) =>
  state.account.cancellationDetailsStateByAccountId;
const cancellationDetailsByAccountIdSelector = (state: State) =>
  state.account.cancellationDetailsByAccountId;
export const cancellationDetailsStateForAccountIdSelector = createSelector(
  [cancellationDetailsStateByAccountIdSelector, (_state: State, accountId: number) => accountId],
  (cancellationDetailsStateByAccountId, accountId) =>
    cancellationDetailsStateByAccountId[accountId],
);
export const cancellationDetailsSelector = createSelector(
  [cancellationDetailsByAccountIdSelector, (_state: State, accountId: number) => accountId],
  (cancellationDetailsByAccountId, accountId) => cancellationDetailsByAccountId[accountId],
);

const messageCountsStateByAccountIdSelector = (state: State) =>
  state.account.messageCountsStateByAccountId;
const messageCountsByAccountIdSelector = (state: State) => state.account.messageCountsByAccountId;
export const messageCountsStateForAccountIdSelector = createSelector(
  [messageCountsStateByAccountIdSelector, (_state: State, accountId: number) => accountId],
  (messageCountsStateByAccountId, accountId) => messageCountsStateByAccountId[accountId],
);
export const messageCountsSelector = createSelector(
  [messageCountsByAccountIdSelector, (_state: State, accountId: number) => accountId],
  (messageCountsByAccountId, accountId) => messageCountsByAccountId[accountId],
);
